// First-run onboarding — trigger + patch-seeding helpers (MDL-323).
//
// The onboarding wizard is offered when a connected device returns a config
// with zero patches. Every exit from the wizard — completed OR skipped — must
// leave at least one patch behind, so the trigger can never mislead: "no
// patches" only ever means "never set up" (the editors already forbid deleting
// the last patch). See spec 144080898 §1, §3.
//
// These helpers are transport- and editor-agnostic so both editors and the
// tests share one implementation.

/**
 * Should the first-run onboarding be offered for this config?
 * True only when the config has an empty (or missing) patches list.
 *
 * @param {object|null|undefined} config - a device config ({ settings, patches })
 * @returns {boolean}
 */
export function shouldOnboard(config) {
  if (!config) return false;
  return !Array.isArray(config.patches) || config.patches.length === 0;
}

/**
 * Build a blank scaffold patch — one renderable patch with no effects. Matches
 * the shape produced by the editors' own "add patch" action so the seeded patch
 * is indistinguishable from a user-created one.
 *
 * @param {object} [opts]
 * @param {() => string} [opts.generateUuid] - UUID generator (injectable for
 *   tests and for the device's non-secure-context fallback).
 * @param {string} [opts.title]
 * @returns {object} a patch object
 */
export function createScaffoldPatch({ generateUuid = defaultUuid, title = 'Patch 1' } = {}) {
  return {
    uuid: generateUuid(),
    title,
    effects: [],
    bpm: 120,
    meter: 4,
    swing: 0,
    clock_source: 'internal',
  };
}

/**
 * Guarantee the config has at least one patch, seeding an empty scaffold patch
 * if it has none. Mutates `config` in place (its `patches` array is the
 * canonical model the editors bind to) and also returns it.
 *
 * Returns a flag so callers can tell whether a seed was actually added (and
 * therefore whether a persist is needed).
 *
 * @param {object} config - a device config; must be a non-null object
 * @param {object} [opts] - forwarded to createScaffoldPatch
 * @returns {{ config: object, seeded: boolean }}
 */
export function ensureAtLeastOnePatch(config, opts = {}) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('ensureAtLeastOnePatch requires a config object');
  }
  if (!Array.isArray(config.patches)) config.patches = [];
  if (config.patches.length > 0) return { config, seeded: false };
  config.patches.push(createScaffoldPatch(opts));
  return { config, seeded: true };
}

/**
 * Append a patch to the config, creating the `patches` array when the device's
 * config has never had one.
 *
 * A config with no `patches` key at all is precisely what makes shouldOnboard()
 * true, so the wizard's own exit path is the one place guaranteed to meet it —
 * pushing straight onto config.patches threw there (MDL-377).
 *
 * @param {object} config - a device config
 * @param {object} patch - the patch to append
 * @returns {number} the index of the appended patch
 */
export function appendPatch(config, patch) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('appendPatch requires a config object');
  }
  if (!Array.isArray(config.patches)) config.patches = [];
  config.patches.push(patch);
  return config.patches.length - 1;
}

/**
 * Build a patch containing a single free-running (Hz) bounce effect from the
 * first-patch step's draft. Free-running (clock_sync: false) so it modulates
 * regardless of clock source — the safe "hear it working" default (spec §2.4).
 *
 * @param {object} draft - { channel, cc, min?, max?, rate? }
 * @param {object} [opts] - forwarded to createScaffoldPatch (generateUuid, title)
 * @returns {object} a patch with one bounce effect
 */
export function createBouncePatch(draft, { generateUuid = defaultUuid, title = 'My first patch' } = {}) {
  const patch = createScaffoldPatch({ generateUuid, title });
  patch.effects = [{
    type: 'bounce',
    channel: draft.channel,
    cc: draft.cc,
    clock_sync: false,
    rate: draft.rate ?? 0.5,
    min: draft.min ?? 0,
    max: draft.max ?? 127,
  }];
  return patch;
}

// crypto.randomUUID() requires a secure context; the on-device editor is served
// over plain http, so it injects its own getRandomValues-based generator. This
// default is only used where randomUUID is available (tests, desktop editor).
function defaultUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Deterministic-enough fallback for non-secure contexts without an injected
  // generator; callers that care (the on-device editor) pass generateUuid.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
